import type { RequestQuestionField } from "@lafayette/schemas";

// The pure part of the question field builder (CHT-751, contract 7.4): rows the staff member edits, the rules the server checks again
// (`requestQuestionFieldSchema` and the domain's `validateQuestionFields`), and the conversion to the body of `askCustomer`. The
// limits repeat the schema's `QUESTION_SCHEMA_LIMITS`; `field-builder.test.ts` compares them with the schema's own.

export const FIELD_LIMITS = Object.freeze({
  maxFields: 8,
  keyChars: 32,
  labelChars: 100,
  optionChars: 60,
  minOptions: 2,
  maxOptions: 12,
});

export const FIELD_TYPES = ["TEXT", "NUMBER", "DATE", "TIME", "CHOICE", "BOOLEAN"] as const;
export type BuilderFieldType = (typeof FIELD_TYPES)[number];

export type BuilderRow = Readonly<{
  /** A stable React key and the base of the control ids. Not sent. */
  rowId: number;
  key: string;
  /** True once the staff member typed the key themselves: then it no longer follows the label. */
  keyTouched: boolean;
  label: string;
  type: BuilderFieldType;
  required: boolean;
  /** One option per line (CHOICE only). */
  optionsText: string;
}>;

export type RowErrors = Readonly<{
  key?: string;
  label?: string;
  options?: string;
}>;

const KEY_PATTERN = /^[a-z][a-zA-Z0-9]*$/;

/** "Number of guests" becomes `numberOfGuests`. An empty or unusable label gives `field`. */
export function keyFromLabel(label: string): string {
  const words = label
    .normalize("NFKD")
    .replace(/[^A-Za-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter((word) => word !== "");
  const joined = words
    .map((word, index) =>
      index === 0
        ? word.charAt(0).toLowerCase() + word.slice(1)
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join("");
  // A key starts with a letter: drop leading digits, then make the first letter lower case ("123 go" gives "go").
  const stripped = joined.replace(/^[^A-Za-z]+/, "");
  const cleaned = stripped.charAt(0).toLowerCase() + stripped.slice(1);
  return (cleaned === "" ? "field" : cleaned).slice(0, FIELD_LIMITS.keyChars);
}

export function newRow(rowId: number): BuilderRow {
  return {
    rowId,
    key: "",
    keyTouched: false,
    label: "",
    type: "TEXT",
    required: true,
    optionsText: "",
  };
}

export const optionsOf = (text: string): readonly string[] =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");

/** The key of a row. While `keyTouched` is false the builder keeps it equal to `keyFromLabel(label)`. */
const effectiveKey = (row: BuilderRow): string => row.key;

export type FieldsValidation =
  | Readonly<{ ok: true; fields: readonly RequestQuestionField[]; errors: readonly [] }>
  | Readonly<{ ok: false; fields: null; errors: readonly RowErrors[]; tooMany: boolean }>;

export function validateRows(rows: readonly BuilderRow[]): FieldsValidation {
  const tooMany = rows.length > FIELD_LIMITS.maxFields;
  const keys = rows.map((row) => effectiveKey(row));
  const errors: RowErrors[] = rows.map((row, index) => {
    const found: { key?: string; label?: string; options?: string } = {};
    const label = row.label.trim();
    if (label === "") found.label = "Write the label the customer will read.";
    else if (label.length > FIELD_LIMITS.labelChars) {
      found.label = `Use at most ${FIELD_LIMITS.labelChars} characters.`;
    }
    const key = keys[index] ?? "";
    if (!KEY_PATTERN.test(key) || key.length > FIELD_LIMITS.keyChars) {
      found.key = `Start with a lower-case letter, then letters and digits only, at most ${FIELD_LIMITS.keyChars}.`;
    } else if (keys.indexOf(key) !== index) {
      found.key = "Another field already uses this key.";
    }
    if (row.type === "CHOICE") {
      const options = optionsOf(row.optionsText);
      if (options.length < FIELD_LIMITS.minOptions || options.length > FIELD_LIMITS.maxOptions) {
        found.options = `Give ${FIELD_LIMITS.minOptions} to ${FIELD_LIMITS.maxOptions} options, one per line.`;
      } else if (options.some((option) => option.length > FIELD_LIMITS.optionChars)) {
        found.options = `Each option can have at most ${FIELD_LIMITS.optionChars} characters.`;
      } else if (new Set(options).size !== options.length) {
        found.options = "Each option must be different.";
      }
    }
    return found;
  });
  if (tooMany || errors.some((entry) => Object.keys(entry).length > 0)) {
    return { ok: false, fields: null, errors, tooMany };
  }
  return {
    ok: true,
    errors: [],
    fields: rows.map((row, index) => ({
      key: keys[index] ?? "",
      label: row.label.trim(),
      type: row.type,
      required: row.required,
      ...(row.type === "CHOICE" ? { options: [...optionsOf(row.optionsText)] } : {}),
    })),
  };
}
